import { DateManager } from 'src/app/core/managers/date.manager';
import { DateEnum } from 'src/app/core/enums/date.enum';

/**
 * Formato destino para fechas: "2026-02-06 02:37 +0000"
 */
const TARGET_FORMAT = DateEnum.YYYY_MM_DD_HH_mm_ZZ;

/**
 * Parsea una fecha que puede venir como string (varios formatos) o Date.
 * Formatos soportados: DD/MM/YYYY ZZ, DD/MM/YYYY, YYYY-MM-DD HH:mm ZZ, YYYY-MM-DD
 */
function parseDateValue(dateValue: Date | string): Date | null {
  if (!dateValue) return null;
  if (dateValue instanceof Date) return DateManager.isValidDate(dateValue) ? dateValue : null;
  const str = String(dateValue).trim();
  if (!str) return null;
  let parsed = DateManager.stringToDate(str, 'DD/MM/YYYY ZZ');
  if (parsed) return parsed;
  parsed = DateManager.stringToDate(str, 'DD/MM/YYYY');
  if (parsed) return parsed;
  parsed = DateManager.stringToDate(str, TARGET_FORMAT);
  if (parsed) return parsed;
  return DateManager.stringToDate(str, DateEnum.YYYY_MM_DD);
}

/**
 * Combina fecha y hora estimada para obtener dateResultante en formato YYYY-MM-DD HH:mm ZZ
 * @param dateValue - Fecha (Date, "05/02/2026 -0500" o "05/02/2026")
 * @param estimatedTime - Hora en formato "04:30"
 */
function buildResultDate(dateValue: Date | string, estimatedTime: string): Date | null {
  const baseDate = parseDateValue(dateValue);
  if (!baseDate || !estimatedTime || typeof estimatedTime !== 'string') return null;
  const timeMatch = estimatedTime.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!timeMatch) return null;
  const hours = parseInt(timeMatch[1], 10);
  const minutes = parseInt(timeMatch[2], 10);
  const result = DateManager.setTimeToDate(baseDate, hours, minutes, 0);
  return result;
}

/**
 * Obtiene el total de minutos pactados (hourPact * 60 + minutePact). Si ambos son 0, retorna 60.
 */
function getTotalPactMinutes(hourPact: number, minutePact: number): number {
  const h = Number(hourPact) || 0;
  const m = Number(minutePact) || 0;
  const total = h * 60 + m;
  return total > 0 ? total : 60;
}

/**
 * Genera un valor aleatorio en minutos dentro del 30% del total pactado, mayor a 0.
 * @returns Cantidad de minutos a restar (entre 1 y 30% del total, inclusive)
 */
function getRandomMinutesToSubtract(totalPactMinutes: number): number {
  const thirtyPercent = Math.floor(totalPactMinutes * 0.3);
  if (thirtyPercent <= 0) return 1;
  return Math.floor(Math.random() * thirtyPercent) + 1;
}

/**
 * Resta minutos de una fecha, manejando correctamente el cruce de día.
 * Ej: 2026-02-06 00:20 - 30 min = 2026-02-05 23:50
 */
function subtractMinutesFromDate(date: Date, minutes: number): Date {
  return DateManager.substract(date, minutes, 'minutes');
}

/**
 * Recalcula arrival y departure cuando la diferencia es <= 30 min y rmmRegistered es false.
 * @param group - Objeto con arrival, departure, estimatedTime, dateLoad/dateDownload, hourPact, minutePact, rmmRegistered
 * @param dateFieldKey - Nombre del campo de fecha: 'dateLoad' (upload) o 'dateDownload' (download)
 */
export function recalculateArrivalDepartureWhenNeeded(
  group: {
    arrival: Date | string;
    departure: Date | string;
    estimatedTime: string;
    [key: string]: any;
  },
  dateFieldKey: 'dateLoad' | 'dateDownload'
): { arrival: Date; departure: Date } | null {
  const arrival = group.arrival;
  const departure = group.departure;
  const rmmRegistered = group.rmmRegistered;

  if (!arrival || !departure) return null;
  if (rmmRegistered === true) return null;

  const arrivalDate = arrival instanceof Date ? arrival : DateManager.stringToDate(String(arrival), TARGET_FORMAT);
  const departureDate = departure instanceof Date ? departure : DateManager.stringToDate(String(departure), TARGET_FORMAT);
  if (!DateManager.isValidDate(arrivalDate) || !DateManager.isValidDate(departureDate)) return null;

  const diffMinutes = DateManager.dateDiff(departureDate, null, arrivalDate, null, 'minutes');
  if (diffMinutes === null || isNaN(diffMinutes) || diffMinutes > 30) return null;

  const dateValue = group[dateFieldKey];
  const estimatedTime = group.estimatedTime;
  const resultDate = buildResultDate(dateValue, estimatedTime);

  if (!resultDate) return null;

  const hourPact = group.hourPact != null ? group.hourPact : 0;
  const minutePact = group.minutePact != null ? group.minutePact : 0;
  const totalPactMinutes = getTotalPactMinutes(hourPact, minutePact);
  const minutesToSubtract = getRandomMinutesToSubtract(totalPactMinutes);

  const recalculatedArrival = subtractMinutesFromDate(resultDate, minutesToSubtract);
  const recalculatedDeparture = DateManager.add(resultDate, totalPactMinutes, 'minutes');

  return {
    arrival: recalculatedArrival,
    departure: recalculatedDeparture
  };
}
